import React, { Fragment, useEffect, useState } from 'react'
import { StandardPage } from '../../components/_EOS/StandardPage'
import {
  GenericOrderBy,
  GenericTable,
  TableHeaderConfig,
  getNewDirection
} from '../../components/_EOS/Table'
import { ApiResponse } from '../../typings/ApiResponse'
import { useApi } from '../../hooks/useApi'
import { usePagination } from '../../hooks/usePagination'
import { Status } from '../../hooks/useFetchState'
import {
  IconButton,
  Box,
  Grid,
  Typography,
  Avatar,
  Tooltip
} from '@mui/material'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import { StockReserveVsAvailableDto } from '../../components/StockReserve/types'
import { getReserveAvaiable } from '../../components/StockReserve/api'
import { RotateLeft, AllInclusive } from '@mui/icons-material'
import { MissingStockAlert } from '../../components/StockReserve/MissingStockAlert'
import { useProductOrigins } from '../../hooks'
import { ProductOrigin } from '../../interfaces/Producto/ProductOrigins'
import { Filter } from '../../components/_WMS/FilterComponent'
import { Search } from '../../components/StockReserve/StockReserveSearch'

interface PageProps {}

const getImage = (imagen: string) =>
  imagen && imagen.length > 0
    ? imagen
    : 'https://via.placeholder.com/150/e5e5e5/808080?text=Sin%20Imagen'

const generateTableHeadersConfig: (
  productOrigins: ProductOrigin[] | undefined
) => TableHeaderConfig[] = (productOrigins: ProductOrigin[] | undefined) => {
  return [
    {
      field: 'productImage',
      title: 'Imagen',
      sortable: false,
      formater: (value: string) => (
        <Avatar variant='square' alt='SkuImage' src={getImage(value)} />
      )
    },
    { field: 'sku', title: 'SKU', sortable: true },
    { field: 'ean13', title: 'Ean13', sortable: true },
    {
      field: 'productOriginId',
      title: 'Marca',
      sortable: true,
      formater: (value: string) =>
        productOrigins?.find((el) => el.productOriginId === parseInt(value))
          ?.name || ''
    },
    { field: 'productName', title: 'Nombre Producto', sortable: true },
    { field: 'warehouseName', title: 'Almacén' },
    { field: 'totalQuantity', title: 'Stock Físico' },
    { field: 'reservedQuantity', title: 'Stock Reservado' },
    { field: 'availableQuantity', title: 'Stock Disponible' },
    {
      field: 'SkuId',
      title: 'Stock Venta En Verde',
      formater: (_, row?: StockReserveVsAvailableDto) => {
        if (!row) return 'N/A'

        // Si no es venta verde, mostrar "No"
        if (!row.isGreenSale) {
          return 'NO'
        }

        // Si es cantidad infinita, mostrar icono de infinito
        if (row.isInfiniteQty) {
          return (
            <Tooltip title={`Stock infinito: ${row.virtualStockSale}`}>
              <AllInclusive />
            </Tooltip>
          )
        }

        // En caso contrario, mostrar el valor de virtualStockSale
        return String(row.virtualStockSale)
      }
    }
  ]
}

export const StockReservePage: React.FC<PageProps> = () => {
  const [snackbar, setSnackbarOptions] = useState({
    message: '',
    status: '',
    open: false
  })

  const pagination = usePagination(5)
  const { state, callApi } = useApi<ApiResponse<StockReserveVsAvailableDto>>()
  const [orderBy, setOrderBy] = useState<
    GenericOrderBy<StockReserveVsAvailableDto>
  >({
    field: 'SkuId',
    direction: 'desc'
  })

  const [selectedFilters, setSelectedFilters] = useState<Filter[]>([])

  const handleSorting = ({
    field,
    direction
  }: GenericOrderBy<StockReserveVsAvailableDto>) => {
    if (field === orderBy.field) {
      const newDirection = getNewDirection(orderBy.direction)
      setOrderBy((prev) => ({ ...prev, direction: newDirection }))
      return
    }

    setOrderBy({ field, direction })
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    // If the pageSize is different, we should set page 0
    const auxPage = pagination.pageSize !== pageSize ? 0 : page

    NewRequest(selectedFilters, auxPage, pageSize)

    pagination.setPagination(auxPage, pageSize)
  }

  const handleSwapFilterAlertStock = () => {
    setSelectedFilters((prev) => {
      // Verificar si ya existe un filtro con key 'StockAlert'
      const stockAlertExists = prev.some(
        (filter) => filter.key === 'StockAlert'
      )

      if (stockAlertExists) {
        return prev // No volver a agregarlo si ya existe
      }

      return [
        ...prev,
        {
          key: 'StockAlert',
          displayValue: 'Sin Stock',
          label: '',
          value: true
        }
      ]
    })
  }

  const {
    getProductOrigins,
    productOriginsState: { productOrigins }
  } = useProductOrigins()

  useEffect(() => {
    getProductOrigins()
  }, [])

  const headerConfig = generateTableHeadersConfig(productOrigins)

  useEffect(() => {
    pagination.setPagination(0, pagination.pageSize)
    NewRequest(selectedFilters, 0, pagination.pageSize)
  }, [orderBy, selectedFilters])

  const NewRequest = (filters?: Filter[], page?: number, pageSize?: number) => {
    callApi(getReserveAvaiable(filters, orderBy, page, pageSize)).catch(
      (error) => {
        console.log(error)
      }
    )
  }
  return (
    <Fragment>
      <StandardPage pageTitle='Stock y Reservas'>
        <Search
          includeRefreshSearch={true}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          onSearchClick={NewRequest}
        />
        <Fragment>
          <Grid container spacing={2}>
            <Grid item spacing={3} xs={12} sm={3} md={4} lg={2} xl={2}>
              <Box
                display='flex'
                justifyContent='flex-start'
                alignItems='flex-start'
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <IconButton
                    color='primary'
                    onClick={() => handleSwapFilterAlertStock()}
                  >
                    <RotateLeft />
                  </IconButton>
                  <Typography variant='body2'>Pendiente Solución</Typography>
                </div>
              </Box>
            </Grid>
            <Grid item spacing={3} xs={12} sm={4} md={4} lg={2} xl={2}>
              <MissingStockAlert
                onClick={() => handleSwapFilterAlertStock()}
                title=''
              />
            </Grid>
            <Grid item spacing={0} xs={12} sm={5} md={4} lg={8} xl={8}></Grid>
          </Grid>
          <GenericTable<StockReserveVsAvailableDto>
            data={state.data?.items}
            headersConfig={headerConfig}
            IDKey='SkuId'
            pagination={{
              totalItem: state.data?.totalItems || 0,
              page: pagination.page,
              pageSize: pagination.pageSize
            }}
            onPaginationChange={handlePaginationChange}
            isLoading={state.status === Status.Loading}
            onSortChange={handleSorting}
            orderField={orderBy}
          />
        </Fragment>
      </StandardPage>
      <SnackBar
        message={snackbar.message}
        open={snackbar.open}
        status={snackbar.status}
        setOpen={() =>
          setSnackbarOptions({
            ...snackbar,
            open: false
          })
        }
      />
    </Fragment>
  )
}
